import React from 'react';
import { AppBar, Toolbar, Typography, Button, IconButton, Menu, MenuItem, Box, useMediaQuery } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LanguageIcon from '@mui/icons-material/Language';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage, languages } from '../../context/LanguageContext';

const Header = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const { language, setLanguage, availableLanguages, t, isSectionVisible, commandPaletteConfig } = useLanguage();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = React.useState(null);
  const isMobile = useMediaQuery('(max-width:960px)');

  // Language menu
  const handleLanguageClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageSelect = (selectedLanguage) => {
    setLanguage(selectedLanguage);
    setAnchorEl(null);
  };

  // Mobile menu
  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  // Navigation items filtered by visibility
  const allNavItems = [
    { key: 'hero', label: t('home'), href: '#home' },
    { key: 'services', label: t('services'), href: '#services' },
    { key: 'about', label: t('about'), href: '#about' },
    { key: 'features', label: t('features'), href: '#features' },
    { key: 'testimonials', label: t('testimonials') || 'Testimonials', href: '#testimonials' },
    { key: 'faq', label: t('faq'), href: '#faq' },
    { key: 'contact', label: t('contact'), href: '#contact' },
  ];
  const navItems = allNavItems.filter(item => !isSectionVisible || isSectionVisible(item.key));

  return (
    <AppBar
      position="sticky"
      elevation={0}
       sx={{
         backgroundColor: isDarkMode ? '#121212' : '#ffffff',
         color: isDarkMode ? '#ffffff' : '#1a1a1a',
         borderBottom: '1px solid',
         borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
         transition: 'all 0.3s ease-in-out',
         pt: 'env(safe-area-inset-top)',
       }}
    >
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box 
            component="img" 
            src="/logo.png" 
            alt="ITFORSEC Logo" 
            sx={{ 
              height: 40, 
              width: 40, 
              mr: 1.5,
              borderRadius: '50%',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              objectFit: 'cover'
            }} 
          />
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              fontWeight: 700, 
              letterSpacing: '0.5px',
              background: 'linear-gradient(45deg, #2E5BFF, #00C896)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            ITFORSEC
          </Typography>
        </Box>

        {/* Desktop Navigation */}
        {!isMobile && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {navItems.map((item) => (
              <Button 
                key={item.href}
                color="inherit" 
                href={item.href}
                sx={{ 
                  mx: 1,
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  color: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(46, 91, 255, 0.05)',
                    color: '#2E5BFF',
                  }
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>
        )}

        {/* Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Command Palette Trigger - OFF by default, managed by Dashboard switch */}
          {Boolean(commandPaletteConfig?.enabled === true) && commandPaletteConfig?.show_in_header === true && (
            <>
              {/* Desktop Search Button with Shortcut Badge */}
              <Button
                onClick={() => window.dispatchEvent(new CustomEvent('itforsec:open-command-palette'))}
                startIcon={<SearchIcon sx={{ fontSize: 18, color: '#2E5BFF' }} />}
                sx={{
                  mr: 1.5,
                  px: 1.6,
                  py: 0.6,
                  borderRadius: '8px',
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                  border: '1px solid',
                  borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
                  color: isDarkMode ? 'rgba(255, 255, 255, 0.75)' : 'rgba(0, 0, 0, 0.65)',
                  textTransform: 'none',
                  fontSize: '0.84rem',
                  display: { xs: 'none', sm: 'inline-flex' },
                  alignItems: 'center',
                  gap: 1.2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(46, 91, 255, 0.12)' : 'rgba(46, 91, 255, 0.06)',
                    borderColor: '#2E5BFF',
                    color: isDarkMode ? '#FFFFFF' : '#0F172A',
                  }
                }}
              >
                <span>{t('search') || 'Search...'}</span>
                <Box
                  component="kbd"
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    px: 0.8,
                    py: 0.2,
                    borderRadius: '4px',
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                    color: isDarkMode ? '#94A3B8' : '#64748B',
                    border: '1px solid',
                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)',
                  }}
                >
                  {typeof window !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl'}+{((commandPaletteConfig?.shortcut_key || 'k').toUpperCase())}
                </Box>
              </Button>

              {/* Mobile Quick Search Icon */}
              <IconButton
                color="inherit"
                aria-label="Search and Commands"
                onClick={() => window.dispatchEvent(new CustomEvent('itforsec:open-command-palette'))}
                sx={{
                  display: { xs: 'inline-flex', sm: 'none' },
                  mr: 0.5,
                  color: isDarkMode ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.75)',
                }}
              >
                <SearchIcon fontSize="small" />
              </IconButton>
            </>
          )}

          {/* Theme Toggle */}
          <IconButton 
            color="inherit" 
            onClick={toggleTheme}
            sx={{ 
              mr: 1,
              transition: 'transform 0.3s',
              '&:hover': { transform: 'rotate(30deg)' }
            }}
          >
            {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>

          {/* Language Selector */}
          <Button 
            color="inherit" 
            onClick={handleLanguageClick}
            startIcon={<LanguageIcon />}
            sx={{ 
              mr: isMobile ? 0 : 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {availableLanguages && availableLanguages[language]
              ? `${availableLanguages[language].flag ? availableLanguages[language].flag + ' ' : ''}${availableLanguages[language].name || language.toUpperCase()}`
              : (languages[language] || language.toUpperCase())}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleLanguageClose}
            keepMounted
          >
            {Object.entries(availableLanguages || languages).map(([code, item]) => {
              const langName = typeof item === 'object' ? item.name : item;
              const flag = typeof item === 'object' && item.flag ? `${item.flag} ` : '';
              return (
                <MenuItem 
                  key={code} 
                  onClick={() => handleLanguageSelect(code)}
                  selected={language === code}
                >
                  {flag}{langName}
                </MenuItem>
              );
            })}
          </Menu>

          {/* Mobile Menu Button */}
          {isMobile && (
            <>
              <IconButton
                color="inherit"
                aria-label="open menu"
                edge="end"
                onClick={handleMobileMenuOpen}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                anchorEl={mobileMenuAnchorEl}
                open={Boolean(mobileMenuAnchorEl)}
                onClose={handleMobileMenuClose}
                keepMounted
              >
                {navItems.map((item) => (
                  <MenuItem 
                    key={item.href} 
                    onClick={handleMobileMenuClose}
                    component="a"
                    href={item.href}
                  >
                    {item.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
